<!--
  SuperAdmin Money -- wired to GET /api/superadmin/money/summary.

  Trailing-30-day money KPIs (gross revenue, net revenue, refunds,
  Stripe fees, pending payouts).  pending_payouts returns null from
  the API until payout tracking exists (Plan 5 / distributor_payouts
  table).  The KPI tile renders "--" when the value is null.

  The Export CSV button stays disabled: a cross-tenant money CSV
  with appropriate PII handling is deferred post-beta.

  ASCII-only.
-->
<template>
    <div data-testid="sa-money">
        <div class="sa-money-head">
            <h1 class="sa-page-title">Money</h1>
            <button
                data-testid="sa-money-export-btn"
                class="sa-btn"
                disabled
                type="button"
            >
                Export CSV
            </button>
        </div>

        <p
            v-if="loading"
            class="sa-state"
            data-testid="sa-money-loading"
        >
            Loading...
        </p>

        <p
            v-else-if="errorMsg"
            class="sa-state sa-state--error"
            data-testid="sa-money-error"
        >
            {{ errorMsg }}
        </p>

        <section
            v-else
            class="sa-kpi-grid"
            data-testid="sa-money-kpis"
            aria-label="Money KPIs (last 30 days)"
        >
            <div class="sa-kpi" data-testid="sa-money-gross">
                <div class="sa-kpi__label">Gross revenue (30d)</div>
                <div class="sa-kpi__value">
                    {{ formatCents(metrics.gross_revenue_30d) }}
                </div>
            </div>
            <div class="sa-kpi" data-testid="sa-money-net">
                <div class="sa-kpi__label">Net revenue (30d)</div>
                <div class="sa-kpi__value">
                    {{ formatCents(metrics.net_revenue_30d) }}
                </div>
            </div>
            <div class="sa-kpi" data-testid="sa-money-refunds">
                <div class="sa-kpi__label">Refunds (30d)</div>
                <div class="sa-kpi__value">
                    {{ formatCents(metrics.refunds_30d) }}
                </div>
            </div>
            <div class="sa-kpi" data-testid="sa-money-fees">
                <div class="sa-kpi__label">Stripe fees (30d)</div>
                <div class="sa-kpi__value">
                    {{ formatCents(metrics.stripe_fees_30d) }}
                </div>
            </div>
            <div class="sa-kpi" data-testid="sa-money-payouts">
                <div class="sa-kpi__label">Pending payouts</div>
                <div class="sa-kpi__value">
                    {{ formatCents(metrics.pending_payouts) }}
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import api from "@/services/api";

function formatCents(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

export default {
    name: "SuperAdminMoney",
    data() {
        return {
            loading: true,
            errorMsg: "",
            metrics: {
                gross_revenue_30d: null,
                net_revenue_30d: null,
                refunds_30d: null,
                stripe_fees_30d: null,
                pending_payouts: null,
            },
        };
    },
    async mounted() {
        await this.fetchMetrics();
    },
    methods: {
        formatCents,
        async fetchMetrics() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const resp = await api.get("/superadmin/money/summary");
                const data = resp && resp.data ? resp.data : resp;
                this.metrics = {
                    gross_revenue_30d: data.gross_revenue_30d ?? null,
                    net_revenue_30d: data.net_revenue_30d ?? null,
                    refunds_30d: data.refunds_30d ?? null,
                    stripe_fees_30d: data.stripe_fees_30d ?? null,
                    pending_payouts: data.pending_payouts ?? null,
                };
            } catch (e) {
                this.errorMsg = "Could not load money data. Please try again.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-money-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}
.sa-kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
}
.sa-kpi {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 14px;
}
.sa-kpi__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--tf-fg-3);
    margin-bottom: 6px;
}
.sa-kpi__value {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
}
.sa-btn {
    background: #edf2f7;
    color: #a0aec0;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 13px;
    cursor: not-allowed;
}
.sa-btn[disabled] {
    opacity: 0.65;
}
</style>
